import { createRouter, createWebHistory } from 'vue-router'
import { TOKEN_KEY } from '@/core/storage'
import { useAuthStore } from '@/core/store/auth'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/login'),
    meta: { public: true }
  },
  {
    path: '/admin/systems',
    name: 'AdminSystems',
    component: () => import('@/pages/admin/systems'),
    meta: { requiresSuper: true }
  },
  {
    path: '/',
    component: () => import('@/pages/layout'),
    children: [
      {
        path: '',
        redirect: '/home'
      },
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/pages/home'),
        meta: { requiresSystemContext: true }
      },
      {
        path: 'admin/accounts',
        name: 'AdminAccounts',
        component: () => import('@/pages/admin/accounts'),
        meta: { requiresAccountAdmin: true }
      },
      {
        path: 'admin/rename-requests',
        name: 'AdminRenameRequests',
        component: () => import('@/pages/admin/rename-requests'),
        meta: { requiresSystemContext: true }
      },
      {
        path: 'admin/ownership-transfer-requests',
        name: 'AdminOwnershipTransferRequests',
        component: () => import('@/pages/admin/ownership-transfer-requests'),
        meta: { requiresSystemContext: true }
      },
      {
        path: 'recycle',
        name: 'Recycle',
        component: () => import('@/pages/recycle'),
        meta: { requiresSystemContext: true }
      },
      {
        path: 'project/:projectId',
        name: 'Project',
        component: () => import('@/pages/project'),
        meta: { requiresSystemContext: true }
      },
      {
        path: 'library/:libraryId',
        name: 'Library',
        component: () => import('@/pages/library'),
        meta: { requiresSystemContext: true }
      },
      {
        path: 'section/:sectionId',
        name: 'Section',
        component: () => import('@/pages/section'),
        meta: { requiresSystemContext: true }
      },
      {
        path: 'section/:sectionId/content/:contentId/edit',
        name: 'ContentEditor',
        component: () => import('@/pages/content-editor'),
        meta: { requiresSystemContext: true }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

router.beforeEach(async (to) => {
  const token = localStorage.getItem(TOKEN_KEY)
  const auth = useAuthStore()

  if (to.meta.public) {
    if (token && to.name === 'Login') {
      if (!auth.user) {
        await auth.fetchMe()
      }
      if (auth.isSuper && !auth.actAsSystemId) {
        return { path: '/admin/systems' }
      }
      if (auth.actAsSystemId || !auth.isSuper) {
        return { path: '/home' }
      }
    }
    return true
  }

  if (!token) {
    return { name: 'Login', query: { redirect: to.fullPath } }
  }

  if (!auth.user) {
    await auth.fetchMe()
  }

  if (to.meta.requiresSuper && !auth.isSuper) {
    return { path: '/home' }
  }

  if (to.meta.requiresSystemContext) {
    if (!auth.currentSystemId) {
      if (auth.user?.isSuper) {
        return { path: '/admin/systems' }
      }
      return { name: 'Login', query: { redirect: to.fullPath } }
    }
  }

  if (to.meta.requiresAccountAdmin && !auth.canManageAccounts) {
    return { path: '/home' }
  }

  if (
    to.name === 'AdminAccounts' &&
    auth.isPlatformSuper &&
    !auth.actAsSystemId
  ) {
    return { path: '/admin/systems' }
  }

  return true
})

export default router
